// Where each onboarding field ends up in the Originator Report PDF
// (pdf_generation_service templates/_originator_pages.tex.j2). The numbers
// are shared by the page thumbnails below and the <PdfSpot> badges next to
// the fields in OnboardPlatformDialog, so whoever fills in the form can see
// what each value changes. Keep in sync with the PDF layout.
export const PDF_SPOTS = {
    logo: { n: 1, where: 'Page header, next to the Exaloan logo (every page)' },
    name: { n: 2, where: 'Page header title "<name> – Loan Book Review" (every page, Overview and every borrower type)' },
    country: { n: 3, where: 'Page 1, Originator Profile: Country tile with flag' },
    borrowerType: { n: 4, where: 'Page 1, Loan types tile (all labels, e.g. "Consumer · SME"); section name above the title ("Originator Report · SME")' },
    currency: { n: 5, where: 'Page 1, Currency tile; every amount in the report' },
    description: { n: 6, where: 'Page 1, text under the profile tiles' },
    operatingModel: { n: 7, where: 'Page 1, Operating model (facts on the right)' },
    creditInsurance: { n: 8, where: 'Page 1, Credit insurance (facts on the right)' },
    purposes: { n: 9, where: 'Page 3, Portfolio Composition: loans by purpose' },
    sectors: { n: 10, where: 'Page 3, Portfolio Composition: loans by sector' },
    creditScores: { n: 11, where: 'Page 3, Distribution and Loan Book Performance by Originator Score' },
    repayment: { n: 12, where: 'Page 4, Repayment Status by Vintage' },
};

export function PdfSpot({ spot }) {
    const { n, where } = PDF_SPOTS[spot];
    return (
        <span className="pr-pdf-spot" title={`Shown in the PDF: ${where}`}>
            {n}
        </span>
    );
}

// One A4 page (210 x 297 units). `boxes`: [x, y, width, height, spot?] -
// boxes with a spot are highlighted and get its number.
function PageThumb({ title, boxes }) {
    return (
        <figure className="pr-pdf-guide-page">
            <svg viewBox="0 0 210 297" role="img" aria-label={title}>
                <rect className="pr-pdf-guide-sheet" x="0" y="0" width="210" height="297" />
                <rect className="pr-pdf-guide-box" x="10" y="10" width="34" height="11" />
                <rect className="pr-pdf-guide-rule" x="10" y="27" width="190" height="0.8" />
                {boxes.map(([x, y, w, h, spot], i) => (
                    <g key={i}>
                        <rect className={spot ? 'pr-pdf-guide-spot' : 'pr-pdf-guide-box'} x={x} y={y} width={w} height={h} />
                        {spot && (
                            <g transform={`translate(${x + w / 2}, ${y + h / 2})`}>
                                <circle className="pr-pdf-guide-marker" r="7.5" />
                                <text className="pr-pdf-guide-number" textAnchor="middle" dy="3.4">
                                    {PDF_SPOTS[spot].n}
                                </text>
                            </g>
                        )}
                    </g>
                ))}
            </svg>
            <figcaption>{title}</figcaption>
        </figure>
    );
}

const HEADER = [
    [52, 10, 24, 11, 'logo'],
    [118, 10, 82, 11, 'name'],
];

const PAGES = [
    {
        title: 'Page 1 – Profile & Key Figures',
        boxes: [
            ...HEADER,
            [10, 40, 56, 22, 'country'],
            [70, 40, 56, 22, 'borrowerType'],
            [130, 40, 46, 22, 'currency'],
            [181, 41, 20, 20],
            [10, 68, 112, 18, 'description'],
            [130, 68, 70, 8, 'operatingModel'],
            [130, 78, 70, 8, 'creditInsurance'],
            ...[0, 1, 2, 3].flatMap((i) => [
                [10 + i * 48, 98, 44, 20],
                [10 + i * 48, 122, 44, 20],
            ]),
            [10, 152, 190, 62],
            [10, 222, 190, 34],
        ],
    },
    {
        title: 'Page 3 – Composition & Score',
        boxes: [...HEADER, [10, 40, 92, 52, 'purposes'], [108, 40, 92, 52, 'sectors'], [10, 104, 190, 62, 'creditScores'], [10, 174, 190, 56]],
    },
    {
        title: 'Page 4 – Defaults & Repayment',
        boxes: [...HEADER, [10, 40, 190, 26], [10, 72, 190, 30], [10, 110, 190, 80], [10, 200, 190, 64, 'repayment']],
    },
];

export default function PdfLayoutGuide() {
    return (
        <div className="pr-pdf-guide">
            <div className="pr-pdf-guide-pages">
                {PAGES.map((page) => (
                    <PageThumb key={page.title} {...page} />
                ))}
            </div>
            <ol className="pr-pdf-guide-legend">
                {Object.entries(PDF_SPOTS).map(([spot, { n, where }]) => (
                    <li key={spot}>
                        <span className="pr-pdf-spot">{n}</span>
                        {where}
                    </li>
                ))}
            </ol>
        </div>
    );
}
